import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../../api/context";
import type { ConsoleMessageView } from "../../../api/console-types";

const PAGE_SIZE = 30;

export type InboxState =
  | { status: "loading" }
  | { status: "ready"; items: ConsoleMessageView[]; nextCursor: string | null }
  | { status: "error"; error: unknown };

/**
 * OWN-317: the console message list (newest first) behind the assistant panel's summaries / questions /
 * proposals tabs. Opening it marks everything read once, which clears the FAB dot.
 */
export function useAssistantInbox(workspaceId: string, onRead: () => void) {
  const api = useApi();
  const [state, setState] = useState<InboxState>({ status: "loading" });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [decideError, setDecideError] = useState<unknown>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => {
    setState({ status: "loading" });
    api
      .listConsoleMessages(workspaceId, { limit: PAGE_SIZE })
      .then(
        (page) => {
          setState({ status: "ready", items: [...page.items], nextCursor: page.nextCursor });
          if (page.items.some((m) => !m.readAt)) {
            return api.markConsoleRead(workspaceId).then(onRead);
          }
          return undefined;
        },
        (error: unknown) => setState({ status: "error", error }),
      )
      .catch((error: unknown) => console.warn("assistant inbox: mark read failed", error));
  }, [api, workspaceId, onRead]);

  useEffect(load, [load]);

  const loadMore = () => {
    if (state.status !== "ready" || !state.nextCursor) return;
    setLoadingMore(true);
    api
      .listConsoleMessages(workspaceId, { cursor: state.nextCursor, limit: PAGE_SIZE })
      .then(
        (page) =>
          setState((s) =>
            s.status === "ready"
              ? { status: "ready", items: [...s.items, ...page.items], nextCursor: page.nextCursor }
              : s,
          ),
        (error: unknown) => setState({ status: "error", error }),
      )
      .finally(() => setLoadingMore(false));
  };

  const decide = (m: ConsoleMessageView, verb: "approve" | "reject") => {
    setBusyId(m.id);
    setDecideError(null);
    api
      .decideConsoleProposal(workspaceId, m.id, verb)
      .then(
        ({ proposal, result }) =>
          setState((s) =>
            s.status === "ready"
              ? {
                  ...s,
                  items: [result, ...s.items.map((x) => (x.id === proposal.id ? proposal : x))],
                }
              : s,
          ),
        (error: unknown) => setDecideError(error),
      )
      .finally(() => setBusyId(null));
  };

  return { state, load, loadMore, loadingMore, decide, busyId, decideError };
}
